import { TableTypeNames } from '../column.service';

/**
 * SD-7878 — the migration roadmap tracker for table preference persistence.
 *
 * Standalone (no service imports) so both the store contract and its implementation can read it
 * without any circular dependency. This map is the single, greppable source of truth for which
 * (table, concern) pairs have moved from localStorage to the backend user-preferences service.
 */

/** Where a given preference is stored. */
export type PersistenceTier = 'local' | 'backend';

/** The distinct preference concerns a table can persist. */
export type TablePreferenceConcern = 'columns' | 'sort' | 'filter' | 'search';

/**
 * Current tier per concern for any table without an explicit override below.
 * Columns already migrated to the backend (SD-6481); sort/filter/search remain on
 * localStorage until their backend migration lands (post-SD-7319).
 */
const DEFAULT_TIERS: Record<TablePreferenceConcern, PersistenceTier> = {
    columns: 'backend',
    sort: 'local',
    filter: 'local',
    search: 'local',
};

/**
 * MIGRATION TRACKER — per-(table, concern) overrides as concerns move to the backend. Empty today:
 * every table uses {@link DEFAULT_TIERS}. As a table migrates, add an override, e.g.:
 *
 *   [TableTypeNames.PMC_VIOLATIONS]: { sort: 'backend', filter: 'backend' },
 */
export const TABLE_PREFERENCE_TIERS:
    Partial<Record<TableTypeNames, Partial<Record<TablePreferenceConcern, PersistenceTier>>>> = {};

/** Resolve the storage tier for a (table, concern), honoring overrides then defaults. */
export function resolveTier(table: TableTypeNames, concern: TablePreferenceConcern): PersistenceTier {
    return TABLE_PREFERENCE_TIERS[table]?.[concern] ?? DEFAULT_TIERS[concern];
}
